/**
 * A party's addresses, through the doors core wrote for them (#2267 port 3).
 *
 * ── what the legacy composer did instead ────────────────────────────────────
 * `AddressSection` in `crm/RecordPage.tsx` wrote addresses with raw table DML —
 * a raw insert into the address table, a second raw insert of the `located_at`
 * edge beside it, and a raw update of that edge's `valid_to` to end a tenure.
 *
 * (Described rather than quoted, deliberately. `relationshipWriters.test.ts`
 * scans console SOURCE TEXT for the raw-edge-write shape, and this file's own
 * source is inside its input — so pasting the three calls verbatim made the
 * ratchet name this module as a new raw relationship writer, which is the exact
 * opposite of what it is. Measured: the census red on `record/partyAddressDoors.ts`
 * with the quote in place, green without it. Same trap `docs/ratchets.md` and
 * merge rule 46 §2 record — an illustration of a scanned idiom has to be
 * written so it cannot match.)
 *
 * Core has had a door for each of those since 0031, guarded on WRITE authority
 * over the party since 0122. Going around them costs four specific things, and
 * none of them is style:
 *
 *   · **the guard.** `attach_address`, `link_address` and `end_address` each
 *     call `core.assert_can_act(party_type, party_id, 'write')` — authority over
 *     the PARTY, which is what editing where a company is located actually is.
 *     The raw path asks `core.relationship`'s own policies instead, which since
 *     `2026081207` do carry an authority predicate — so this is a governance and
 *     evidence defect rather than an open door, and it is written down that way
 *     rather than escalated as a leak.
 *   · **the party-kind refusal.** `attach_address` raises
 *     `'% cannot carry addresses'` for anything but a person or an organisation.
 *     A raw insert will happily hang an address off a quote.
 *   · **the purpose validation.** Both doors refuse a purpose that is not in
 *     `core.address_purpose`. The raw insert wrote `attributes: {purpose}`
 *     unchecked, so a typo produced an edge whose purpose resolves to no label
 *     and no tone for the rest of its life.
 *   · **the events.** `core.emit('core.address.attached' | '.linked' | '.ended')`
 *     simply never fired. Anything subscribing to where a party is located —
 *     now or later — saw nothing at all.
 *
 * And one thing the legacy path got RIGHT and is kept: a move is end-dated,
 * never deleted.
 *
 * ── WHAT THIS FILE IS NOT, and the measurement that shrank it ──────────────
 * It started as four doors plus a bespoke `located_at` panel, on the belief that
 * a generic relationship band could not show an address's PURPOSE. That belief
 * was wrong, and the correction is worth keeping written down because the
 * evidence is one `git grep` away and I walked past it:
 *
 * `core/0031:76` registers `located_at` with no attributes, which is where I
 * stopped reading. Core `2026080302` — the migration that INTRODUCED
 * `relationship_type.attributes_schema` — then declares it:
 *
 *     'purpose' → { label: 'Purpose', kind: 'option', sort: 1,
 *                   options_source: 'core.address_purpose' }
 *
 * with its own comment saying "the purpose was always an edge fact; there was
 * simply nowhere to declare it". `RelationBand` renders and EDITS declared edge
 * attributes and validity, and `core.relationship_attributes` resolves the
 * picklist at read time. So the generic band already draws a party's addresses
 * with their purposes and their tenure — the panel was a duplicate of a
 * capability that shipped a month earlier. It is deleted. (Skill rule 1: the
 * live definition is whichever migration defines it LAST.)
 *
 * What survives is the WRITE half, and only where it has a caller.
 *
 * ── the write half is still going around the doors, and that is a PLATFORM gap
 * `useLinkRelation` inserts `core.relationship` directly for every edge slot, and
 * ending a tenure in the band is an update of `valid_to` on the same table. Both
 * skip these doors. The fix is NOT a `located_at` special case in the console —
 * that is the same mistake the deleted panel was, one layer down. `core.
 * relationship_type` has no way to declare that a relationship's writes are
 * doored (there is no `write_door` on it the way there is on `core.entity_type`),
 * so the generic writer cannot know. Raised on #2267 as a platform gap.
 *
 * `link_address` and `end_address` are therefore NOT wrapped here: a hook with no
 * caller is dead code that reads as coverage. They come back when the
 * declaration exists and the generic writer can route by it.
 *
 * ── the one write that is not an RPC, and why it is still a door ────────────
 * `lat`/`lng` (core 0032) are not parameters of `attach_address`, so coordinates
 * cannot ride the door. They are written through `useSaveEntity('core_address')`
 * — the console's canonical writer against an entity whose write door is
 * DECLARED `direct` (`2026080718`), which means the table's own DML *is* its
 * door and RLS is the gate. That is a governed write, not a bypass; what it is
 * not is `client.raw()`, which would leave the console's cache and refusal
 * handling out of the path.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useSolum } from '../data/client'
import { reconcileRelationships } from '../data/queries'
import type { CompanyHit } from './companyLookup'

/** Exactly what `core.party_addresses` returns (core 0031). */
export interface PartyAddress {
  edge_id: string
  address_id: string
  purpose: string | null
  purpose_label: string | null
  purpose_tone: string | null
  is_current: boolean
  valid_from: string | null
  valid_to: string | null
  street: string | null
  street2: string | null
  postal_code: string | null
  city: string | null
  region: string | null
  country_code: string | null
  country: string | null
}

/** The purpose a business register's own address carries. It must exist in
 *  `core.address_purpose` or the door refuses — which is the point. */
export const REGISTERED_PURPOSE = 'registered'

/** Invalidate everything a located_at change can be read through. */
function useAddressReconcile(entity: string, id: string) {
  const qc = useQueryClient()
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: ['party-addresses', entity, id] }),
    reconcileRelationships(qc, entity, id),
  ]).then(() => undefined)
}

export interface AttachAddressInput {
  purpose: string
  street: string
  city: string
  countryCode: string
  postalCode?: string | null
  street2?: string | null
  region?: string | null
}

/**
 * Attach a NEW address to this party. One call, one transaction, one event.
 *
 * The legacy path was two round trips — insert the node, then insert the edge —
 * so an interrupted attach left an orphan `core.address` row with nothing
 * pointing at it and nothing to find it by.
 */
export function useAttachAddress(entity: string, id: string) {
  const { client } = useSolum()
  const reconcile = useAddressReconcile(entity, id)
  return async (input: AttachAddressInput): Promise<string> => {
    const addressId = await client.raw().rpcScalar<string>('attach_address', {
      p_party_type: entity, p_party_id: id, p_purpose: input.purpose,
      p_street: input.street, p_city: input.city,
      p_country_code: input.countryCode.toUpperCase(),
      p_postal_code: input.postalCode ?? null,
      p_street2: input.street2 ?? null,
      p_region: input.region ?? null,
    }, 'core')
    await reconcile()
    return addressId
  }
}

/**
 * The registry lookup's "attach the registered address" option (#2267 port 1).
 *
 * Its own function because the decision it encodes is the lookup's, not the
 * address composer's: a business register states a REGISTERED SEAT, which is a
 * specific `core.address_purpose`, and a hit with no street is not an address
 * to attach.
 */
export function useAttachRegisteredAddress(entity: string, id: string) {
  const attach = useAttachAddress(entity, id)
  return async (hit: CompanyHit): Promise<string | null> => {
    if (!hit.street || !hit.city) return null
    return attach({
      purpose: REGISTERED_PURPOSE,
      street: hit.street,
      city: hit.city,
      countryCode: hit.country,
      postalCode: hit.postal ?? null,
    })
  }
}

